import { create } from 'zustand'
import { authStore } from '../auth/store'
import { nfcCheckin } from '../api/attendance'
import { canSendTag, dequeueTag, getQueuedTags, queuedCount, type QueuedTag } from './queue'
import { getSubdomain } from '../org'

/**
 * 오프라인 큐 자동 동기화 — apps/client 에서 이식.
 *
 * ★ 가장 중요한 규칙: **일시 실패와 영구 실패를 구분한다.**
 *   일시 실패(5xx·429·401·네트워크)를 큐에서 빼면 그 출근 기록은 영구히 사라진다.
 *   §원칙 1(NFC 태깅 시각 기록의 정확성) 정면 위반이다.
 *   반대로 영구 실패(4xx)를 큐에 두면 무한 재시도가 된다. 그래서 나눈다.
 *
 * 그리고 큐에서 뺀 건은 **반드시 사용자에게 보인다**. 조용히 사라지면
 * 직원은 자기 출근이 기록된 줄 안다.
 */

export interface DroppedTag {
  tag: QueuedTag
  error: string
  /** 7일+ 과거라 서버가 거부 → 수기 등록으로 유도해야 하는 건 */
  stale: boolean
}

interface SyncState {
  pending: number
  dropped: DroppedTag[]
  setPending: (n: number) => void
  addDropped: (d: DroppedTag) => void
  clearDropped: () => void
}

export const useSync = create<SyncState>((set) => ({
  pending: 0,
  dropped: [],
  setPending: (pending) => set({ pending }),
  addDropped: (d) => set((s) => ({ dropped: [...s.dropped, d] })),
  clearDropped: () => set({ dropped: [] }),
}))

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

export async function refreshPendingCount(): Promise<void> {
  try {
    useSync.getState().setPending(await queuedCount())
  } catch {
    /* IndexedDB 접근 불가 — 배지만 미갱신 */
  }
}

let syncing = false

/**
 * 큐를 순서대로 전송. 동시 호출 가드 —
 * online 이벤트와 앱 시작 동기화가 겹쳐도 한 번만 돈다.
 */
export async function syncOfflineQueue(): Promise<void> {
  if (syncing) return
  if (!authStore.get().accessToken) return

  syncing = true
  try {
    const pending = await getQueuedTags()
    const currentOrg = getSubdomain()
    let first = true

    for (const tag of pending) {
      /*
       * 🔴 다른 회사로 로그인한 상태면 **보내지 않고 건너뛴다** (2026-09-08).
       *
       * 보내면 서버가 403 «다른 조직의 자원에 접근할 수 없습니다» 를 낸다.
       * 403 은 4xx 라 아래 `permanent` 판정에 걸려 **큐에서 빠진다** — 실제로 찍은
       * 시각이 사라진다. 화면에는 뜨지만 되찾을 수 없다.
       *
       * 큐에 남겨 두면 원래 회사로 다시 로그인했을 때 그대로 나간다.
       * 버리는 것보다 미루는 쪽이 §원칙 1 에 맞다.
       *
       * ⚠ 여기서 `break` 가 아니라 `continue` 다. 두 회사 것이 섞여 있으면
       *   지금 회사 것은 보내야 한다.
       */
      if (!canSendTag(tag, currentOrg)) continue

      // 한 기기의 다건이 한꺼번에 몰리지 않게 건당 소폭 지터
      if (!first) await sleep(Math.random() * 200)
      first = false

      try {
        const res = await nfcCheckin({
          nfcUid: tag.nfcUid,
          requestType: tag.requestType,
          nfcSignature: tag.nfcSignature,
          clientId: tag.clientId,
          clientTimestamp: tag.clientTimestamp,
        })

        if (res.success) {
          // 성공 또는 clientId 멱등 응답 — 큐에서 제거
          await dequeueTag(tag.clientId)
          continue
        }

        const status = res.status ?? 0
        const permanent = status >= 400 && status < 500 && status !== 401 && status !== 429

        if (!permanent) {
          // 5xx · 429 · 401 · 상태 불명 = 일시 실패. 큐에 남기고 다음 기회로.
          break
        }

        // 4xx 영구 실패 — 큐에서 빼되 반드시 화면에 노출한다
        const stale = status === 400 && /7일|과거/.test(res.error ?? '')
        useSync.getState().addDropped({ tag, error: res.error ?? `오류 ${status}`, stale })
        await dequeueTag(tag.clientId)
      } catch {
        // 네트워크가 여전히 끊김 — 큐 유지, 다음 online 이벤트에 재시도
        break
      }
    }
  } finally {
    syncing = false
    await refreshPendingCount()
  }
}

/**
 * 앱 전역 1회 등록.
 *
 * 수백 대가 동시에 와이파이를 되찾으면 재전송이 한꺼번에 몰린다.
 * online 이벤트에 0~5초 무작위 지터 + 디바운스를 걸어 서버 도착을 분산한다.
 */
export function initOfflineSync(): () => void {
  if (typeof window === 'undefined') return () => {}

  let timer: ReturnType<typeof setTimeout> | null = null
  const onOnline = () => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(
      () => {
        timer = null
        void syncOfflineQueue()
      },
      Math.random() * 5000,
    )
  }
  window.addEventListener('online', onOnline)

  // 앱 시작 시 1회 — 이전 세션에서 못 보낸 건 정리.
  // 앱 오픈 시각은 자연히 분산되므로 지터 불필요.
  void syncOfflineQueue()
  void refreshPendingCount()

  return () => {
    if (timer) clearTimeout(timer)
    window.removeEventListener('online', onOnline)
  }
}
